/**
 * Civil Disclosure Workspace
 * 
 * Party-side workspace for navigating and understanding the governed civil disclosure chain
 * 
 * This component consumes the Civil Disclosure Summary API to avoid bundling
 * server-only civilDisclosure modules into the client.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CivilDisclosureSliceCard } from './components/CivilDisclosureSliceCard';
import { Card } from './components/Card';
import { Badge } from './components/Badge';
import { MetricTile } from './components/MetricTile';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase/init';

interface CivilDisclosureWorkspaceProps {
  user: any;
  onNavigateBack: () => void;
}

interface CivilDisclosureSummaryResponse {
  proofOnly: boolean;
  executionAuthority: boolean;
  sourceState: 'authoritative' | 'non-authoritative';
  sliceDefinition: {
    sliceId: string;
    sliceVersion: string;
    description: string;
    moduleChain: {
      evidence: { moduleId: string; moduleVersion: string };
      exhibitMap: { moduleId: string; moduleVersion: string };
      complaint: { moduleId: string; moduleVersion: string };
      casePackage: { moduleId: string; moduleVersion: string };
    };
    authoritativeArtifact: {
      path: string;
      description: string;
    };
  };
  bindingStatus: {
    kind: string;
    receiptVerificationState: string;
    isAuthoritative: boolean;
  };
  metadata: {
    timestamp: string;
    userId: string;
    environment: string;
  };
}

export const CivilDisclosureWorkspace: React.FC<CivilDisclosureWorkspaceProps> = ({
  user,
  onNavigateBack,
}) => {
  const navigate = useNavigate();
  const [summary, setSummary] = useState<CivilDisclosureSummaryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadCivilDisclosureSummary = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Call the Civil Disclosure Summary API
        const getCivilDisclosureSummary = httpsCallable(functions, 'getCivilDisclosureSummary');
        const result = await getCivilDisclosureSummary();
        const data = result.data as CivilDisclosureSummaryResponse;
        
        setSummary(data);
      } catch (err) {
        console.error('Failed to load civil disclosure summary:', err);
        setError(err instanceof Error ? err.message : 'Failed to load civil disclosure summary');
      } finally {
        setLoading(false);
      }
    };

    loadCivilDisclosureSummary();
  }, []);

  const handleNavigateToModule = (moduleId: string) => {
    // Navigate to module-specific views if they exist
    // For now, navigate to proof explorer with module context
    navigate(`/proof-explorer?module=${moduleId}`);
  };

  const handleNavigateToArtifact = (artifactPath: string | undefined) => {
    // Navigate to artifact verification view
    if (artifactPath) {
      navigate(`/verify-bundle?artifact=${encodeURIComponent(artifactPath)}`);
    }
  };

  const handleVerifyBinding = () => {
    // Navigate to receipt verifier with binding context
    navigate('/receipt-verifier?type=civil-disclosure-binding');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0a0a0f] via-[#1a1a2e] to-[#16213e] text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#00d4aa] mx-auto mb-4" />
          <p className="text-[#cbd5e1]">Loading Civil Disclosure Workspace...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0a0a0f] via-[#1a1a2e] to-[#16213e] text-white flex items-center justify-center">
        <Card className="max-w-md">
          <div className="text-center">
            <h3 className="text-lg font-semibold text-red-400 mb-2">Civil Disclosure Workspace Error</h3>
            <p className="text-gray-300 mb-4">
              Failed to load Civil Disclosure summary: {error}
            </p>
            <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
              <p className="text-xs text-red-400">
                API Endpoint: getCivilDisclosureSummary<br />
                Error: {error}
              </p>
            </div>
            <button
              onClick={onNavigateBack}
              className="px-4 py-2 bg-[#00d4aa]/20 text-[#00d4aa] border border-[#00d4aa]/50 rounded hover:bg-[#00d4aa]/30 transition-colors"
            >
              Back to Console
            </button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a0a0f] via-[#1a1a2e] to-[#16213e] text-white">
      {/* Header */}
      <header className="border-b border-white/10 px-6 py-4 bg-[#0a0a0f]/50 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={onNavigateBack}
              className="text-gray-400 hover:text-white transition-colors"
            >
              ← Back
            </button>
            <h1 className="text-2xl font-bold bg-gradient-to-r from-[#00d4aa] to-[#00a8cc] bg-clip-text text-transparent">
              Civil Disclosure Workspace
            </h1>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-400">{user.email}</span>
            <Badge variant="info">Party-Side View</Badge>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto p-6">
        {/* Overview Section */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold mb-4">Slice Overview</h2>
          <CivilDisclosureSliceCard
            sliceDefinition={summary?.sliceDefinition}
            bindingStatus={summary?.bindingStatus}
            onNavigateToModule={handleNavigateToModule}
            onNavigateToArtifact={handleNavigateToArtifact}
            onVerifyBinding={handleVerifyBinding}
          />
        </div>

        {/* Module Status Grid */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold mb-4">Module Status</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {summary?.sliceDefinition?.moduleChain && Object.entries(summary.sliceDefinition.moduleChain).map(([key, module]: [string, any]) => {
              return (
                <Card key={key} className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-medium text-white capitalize">
                      {key.replace(/([A-Z])/g, ' $1').trim()}
                    </h3>
                    <Badge variant="success">
                      Defined
                    </Badge>
                  </div>
                  <p className="text-xs text-gray-400 mb-2">
                    {module.moduleId}@{module.moduleVersion}
                  </p>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Verification Section */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold mb-4">Verification & Reports</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <MetricTile
              label="Binding Verification"
              value={summary?.bindingStatus?.isAuthoritative ? 'Valid' : 'Not Verified'}
              trend={summary?.bindingStatus?.receiptVerificationState || 'No verification status'}
            />
            <MetricTile
              label="Authoritative Artifact"
              value="Case Package"
              trend={summary?.sliceDefinition?.authoritativeArtifact?.path}
            />
            <MetricTile
              label="Source State"
              value={summary?.sourceState === 'authoritative' ? 'Authoritative' : 'Non-Authoritative'}
              trend={summary?.proofOnly ? 'Proof-only view' : 'Full access'}
            />
          </div>
        </div>

        {/* Navigation Section */}
        <div>
          <h2 className="text-xl font-semibold mb-4">Quick Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <button
              onClick={() => handleNavigateToArtifact(summary?.sliceDefinition?.authoritativeArtifact?.path)}
              className="p-4 bg-white/5 border border-white/10 rounded-lg hover:bg-white/10 transition-colors text-left"
            >
              <h3 className="text-sm font-medium text-white mb-1">View Case Package</h3>
              <p className="text-xs text-gray-400">Inspect authoritative artifact</p>
            </button>
            <button
              onClick={() => navigate('/proof-explorer')}
              className="p-4 bg-white/5 border border-white/10 rounded-lg hover:bg-white/10 transition-colors text-left"
            >
              <h3 className="text-sm font-medium text-white mb-1">Proof Explorer</h3>
              <p className="text-xs text-gray-400">Browse evidence and artifacts</p>
            </button>
            <button
              onClick={handleVerifyBinding}
              className="p-4 bg-white/5 border border-white/10 rounded-lg hover:bg-white/10 transition-colors text-left"
            >
              <h3 className="text-sm font-medium text-white mb-1">Verify Binding</h3>
              <p className="text-xs text-gray-400">Check policy-pack binding</p>
            </button>
            <button
              onClick={() => navigate('/marketplace')}
              className="p-4 bg-white/5 border border-white/10 rounded-lg hover:bg-white/10 transition-colors text-left"
            >
              <h3 className="text-sm font-medium text-white mb-1">Marketplace</h3>
              <p className="text-xs text-gray-400">Browse modules and packs</p>
            </button>
          </div>
        </div>

        {/* Info Section */}
        <div className="mt-8 p-4 bg-[#00d4aa]/10 border border-[#00d4aa]/30 rounded-xl">
          <h3 className="text-sm font-medium text-[#00d4aa] mb-2">About This Workspace</h3>
          <p className="text-xs text-[#00d4aa]">
            This is a party-side workspace for inspecting the governed civil evidence and disclosure workflow.
            It provides read-only access to module status, artifact states, and verification information.
            This workspace does not enable bilateral collaboration or settlement negotiations.
          </p>
        </div>
      </main>
    </div>
  );
};
